import Link from 'next/link';
import { FC } from 'react';
import { NEW_TAB_ATTRIBUTES, PATHS } from 'src/common/constants';
import styles from 'src/content/ContentPages.module.scss';

type CookiesPolicyProps = {
  effectiveDate: string;
};

const CookiesPolicy: FC<CookiesPolicyProps> = ({ effectiveDate }) => (
  <main className={styles.root}>
    <h1 className={styles.title}>Cookies Policy</h1>
    <h2 className={styles.subTitle}>
      {`EFFECTIVE ${effectiveDate}`}
    </h2>

    <p>
      Getmyboat uses cookies, mobile identifiers, tracking URLs, log data, and similar
      technologies to help provide, protect, and improve the Getmyboat platform. This
      Cookie Policy (&quot;Policy&quot;) supplements the
      {' '}
      <Link href={PATHS.PRIVACY_POLICY}>Getmyboat Privacy Policy</Link>
      {' '}
      explains how and why we use these technologies and the choices you have.
    </p>

    <h3>Why Getmyboat Uses These Technologies</h3>
    <p>We use these technologies for several purposes, such as:</p>
    <ul>
      <li>
        To enable you to use and access the Getmyboat platform and payment services.
      </li>
      <li>
        To facilitate and streamline the functioning of and your access to the Getmyboat platform.
      </li>
      <li>
        To better understand how you navigate through and interact with the Getmyboat platform and
        to improve it.
      </li>
      <li>
        To serve you tailored advertising on the Getmyboat platform, in emails, and on third-party
        websites.
      </li>
      <li>
        To show you content (e.g., advertisements) that is more relevant to you.
      </li>
      <li>
        To monitor and analyze the performance, operation, and effectiveness of the
        Getmyboat platform and Getmyboat advertisements.
      </li>
      <li>
        To enforce legal agreements that govern the use of the Getmyboat platform.
      </li>
      <li>
        For fraud detection and prevention, trust and safety, and investigations.
      </li>
      <li>
        For purposes of our own customer support, analytics, research, product development, and
        regulatory compliance.
      </li>
    </ul>

    <h3>Cookies</h3>
    <p>
      When you visit the Getmyboat platform, we may place cookies on your device.
      Cookies are small text files that websites send to your computer or other
      internet-connected devices to uniquely identify your browser or to store
      information or settings in your browser. Cookies allow us to recognize you
      when you return. They also help us provide a customized experience and can
      enable us to detect certain kinds of fraud.
    </p>
    <p>
      There are two types of cookies used on the Getmyboat platform: (1) &quot;session cookies&quot;
      and (2) &quot;persistent cookies.&quot; Session cookies normally expire when you close your
      browser, while persistent cookies remain on your device after you close your browser
      and can be used again the next time you access the Getmyboat platform.
    </p>
    <p>
      In many cases, you can manage cookie preferences and opt out of having cookies and
      other data collection technologies used by adjusting the settings on your browser.
      All browsers are different, so visit the &quot;help&quot; section of your browser to learn
      about cookie preferences and other privacy settings available. Please note that if
      you choose to remove or reject cookies or clear local storage, this could affect
      the features, availability, and functionality of the Getmyboat platform.
    </p>

    <h3>Flash Cookies</h3>
    <p>
      We may use Flash Cookies, also known as Local Stored Objects, and similar
      technologies to personalize and enhance your online experience. A Flash cookie
      is a small data file placed on a computer using Adobe Flash technology. The
      Adobe Flash Player is an application that allows rapid development of dynamic
      content, such as video clips and animation.
    </p>
    <p>
      We use Flash cookies to personalize and enhance your online experience and to
      deliver content for Flash players. We may also use Flash cookies for security
      purposes, to gather certain website metrics, and to help remember settings and
      preferences. Flash cookies are managed through a different interface than the
      one provided by your web browser. To manage Flash cookies, please visit Adobe&apos;s
      website.
    </p>
    <p>
      If you disable Flash cookies or other similar technologies, please note that you
      may not have access to certain content and product features, such as your device
      remembering a listing that you viewed during a previous visit.
    </p>

    <h3>Pixel Tags, Web Beacons, and Trackers</h3>
    <p>
      Pixel tags, web beacons, and tracking URLs are tiny graphic images and/or small
      blocks of code placed on website pages, ads, or in our emails that allow us to
      determine whether you performed a specific action. When you access these pages or
      open an email, they let us know you have accessed the web page or opened the email.
      These tools help us measure responses to our communications and improve our web
      pages and promotions.
    </p>

    <h3>Server Logs and Other Technologies</h3>
    <p>
      We collect many different types of information from server logs and other technologies.
      For example, we collect information about the device you use to access the Getmyboat
      platform, your operating system type, browser type, domain, and other system settings,
      as well as the language your system uses and the country and time zone where your
      device is located. Our server logs also record the IP address of the device you use
      to connect to the Internet. An IP address is a unique identifier that devices require
      to identify and communicate with each other on the Internet. We may also collect
      information about the website you were visiting before you came to the Getmyboat
      platform and the website you visit after you leave the Getmyboat platform.
    </p>
    <p>
      We may also collect information about your use of the Getmyboat platform, such as
      when we provide accessibility tools. The tools described help us improve user
      experience and deliver our services.
    </p>

    <h3>Device Information</h3>
    <p>
      We may use device-related information to authenticate users. For example, we may
      use your IP address, browser information, or other data provided by your browser
      or device to identify the device being used to access our platform. We may also
      use these device-related techniques for associating you with different devices
      you may use to access our content, including for fraud-protection purposes and
      to better target advertising.
    </p>

    <h3>Third Parties</h3>
    <p>
      Getmyboat permits third parties to collect the information described above through
      our Service and discloses such information to third parties for business purposes
      as described in this Privacy Policy, including but not limited to providing advertising
      on our Service and elsewhere based on users&apos; online activities over time and across
      different sites, services, devices.
    </p>
    <p>
      Third parties, including Facebook, place technologies such as pixels and SDKs on the
      Getmyboat platform. These technologies (1) help us analyze how you use the Getmyboat
      platform, such as by noting the third-party services from which you arrived, (2) market
      and advertise Getmyboat services to you on the Getmyboat platform and third-party
      websites, (3) help us detect or prevent fraud or conduct risk assessments, and (4)
      collect information about your activities on the Getmyboat platform, other sites,
      and/or the ads you have clicked on. For example, to help us better understand how
      people use the Getmyboat platform, we work with a number of analytics partners,
      including Google Analytics. To prevent Google Analytics from using your information
      for analytics, you may install the Google Analytics Opt-Out Browser by clicking
      {' '}
      <a href="https://tools.google.com/dlpage/gaoptout" {...NEW_TAB_ATTRIBUTES}>here</a>
      . In some cases, cookies are placed if certain criteria is met, such as being logged
      into the third-party service on the same browser.
    </p>
    <p>
      Third parties, including Facebook, may also use such tracking technologies to collect
      or receive information from the Getmyboat platform and elsewhere and use that
      information to serve ads that they believe are most likely to be of interest to you
      and measure the effectiveness of their ads both on the Getmyboat platform and on other
      websites and online services. Targeting and advertising cookies we use may include
      Google, and other advertising networks and services we use from time to time. See
      {' '}
      <a href="https://policies.google.com/technologies/partner-sites" {...NEW_TAB_ATTRIBUTES}>here</a>
      {' '}
      for information on how Google manages data in its ad products. For more information
      about targeting and advertising cookies and how you can opt out, you can visit the
      {' '}
      <a href="https://www.networkadvertising.org/choices/" {...NEW_TAB_ATTRIBUTES}>
        Network Advertising Initiative&apos;s opt-out page
      </a>
      , the
      {' '}
      <a href="https://www.aboutads.info/choices/" {...NEW_TAB_ATTRIBUTES}>
        Digital Advertising Alliance&apos;s opt-out page
      </a>
      , or
      {' '}
      <a href="http://youronlinechoices.eu" {...NEW_TAB_ATTRIBUTES}>http://youronlinechoices.eu</a>
      . To opt out of Google Analytics for
      display advertising or customize Google display network ads, you can visit the
      {' '}
      <a href="https://www.google.com/settings/ads" {...NEW_TAB_ATTRIBUTES}>Google Ads Settings page</a>
      . To the extent advertising technology is integrated into the
      {' '}
      Getmyboat platform and you opt-out of tailored advertising, you may still receive
      advertising content. In that case, the advertising content will just not be tailored
      to your interests. Also, we do not control any of these opt-out links and are not
      responsible for the availability or accuracy of these mechanisms. Users can opt-out
      of the collection and use of information for ad targeting by updating their Facebook
      account ad settings and by contacting dpo@getmyboat.com with a description of your
      request and validation information. For more information on how we share information
      with partners, see the
      {' '}
      <Link href={PATHS.PRIVACY_POLICY}>Getmyboat Privacy Policy</Link>
      .
    </p>

    <h3>Your Choices</h3>
    <p>
      Most browsers automatically accept cookies, but you can modify your browser setting
      to decline cookies by visiting the Help portion of your browser&apos;s toolbar. While you
      may disable cookies through your browser settings, the Getmyboat platform currently
      does not respond to a &quot;Do Not Track&quot; signal in the HTTP header from your browser or
      mobile application due to lack of standardization regarding how that signal should be
      interpreted.
    </p>
    <p>
      Flash cookies operate differently than browser cookies, and cookie management tools
      available in a web browser will not remove flash cookies. To learn more about how to
      manage flash cookies, you can visit the
      {' '}
      <a href="https://www.adobe.com/home" {...NEW_TAB_ATTRIBUTES}>Adobe website</a>
      {' '}
      and make changes at the
      {' '}
      <a
        href="https://www.macromedia.com/support/documentation/en/flashplayer/help/settings_manager02.html"
        {...NEW_TAB_ATTRIBUTES}
      >
        Global Privacy Settings Panel
      </a>
      {' '}
      .
    </p>
    <p>
      Your mobile device may allow you to control cookies through its settings function.
      Refer to your device manufacturer&apos;s instructions for more information.
    </p>
    <p>
      If you choose to decline cookies, some parts of the Getmyboat platform may not work
      as intended or may not work at all.
    </p>
  </main>
);

export default CookiesPolicy;
